// services/escalationService.js
// API helpers + shared constants for the Escalations & Claims module.
// All persistence flows through the real backend API (no local/fake data).
import axios from "axios";
import Cookies from "js-cookie";
import config from "../config.js";

const api = config.apiBaseUrl;

const getHeaders = (token) => {
  const activeToken = token || Cookies.get("token") || localStorage.getItem("token");
  if (!activeToken) {
    throw new Error("Missing or invalid Authorization header");
  }
  return { Authorization: `Bearer ${activeToken}` };
};

export const CARRIERS = ["Delivery Surface", "Amazon", "DTDC", "Standard", "Bus Courier", "Other"];
export const ACTION_TYPES = ["Escalation", "Claim", "Follow-up"];
export const CHANNELS = ["WhatsApp", "Email", "Call"];
export const STATUSES = [
  "Need to Follow Up",
  "Follow-up Completed",
  "Waiting for Response",
  "Resolved",
  "Rejected",
];
export const OPEN_STATUSES = ["Need to Follow Up", "Follow-up Completed", "Waiting for Response"];

export const STATUS_META = {
  "Need to Follow Up": { color: "#b45309", bg: "#fef3c7", border: "#fcd34d" },
  "Follow-up Completed": { color: "#1d4ed8", bg: "#dbeafe", border: "#93c5fd" },
  "Waiting for Response": { color: "#6d28d9", bg: "#ede9fe", border: "#c4b5fd" },
  Resolved: { color: "#047857", bg: "#d1fae5", border: "#6ee7b7" },
  Rejected: { color: "#b91c1c", bg: "#fee2e2", border: "#fca5a5" },
};

export const QUICK_VIEWS = [
  { key: "all", label: "All Cases" },
  { key: "today", label: "Today's Follow-ups" },
  { key: "overdue", label: "Overdue" },
  { key: "unresolvedIssues", label: "Unresolved Issues" },
  { key: "unresolvedClaims", label: "Unresolved Claims" },
  { key: "waiting", label: "Waiting for Response" },
  { key: "resolved", label: "Resolved" },
  { key: "rejected", label: "Rejected" },
];

export const REPORT_TYPES = [
  { key: "daily-followups", label: "Daily Follow-ups" },
  { key: "overdue-followups", label: "Overdue Follow-ups" },
  { key: "unresolved-issues", label: "Unresolved Issues" },
  { key: "unresolved-claims", label: "Unresolved Claims" },
  { key: "carrier-outstanding", label: "Carrier-wise Outstanding" },
  { key: "employee-pending", label: "Employee-wise Pending" },
  { key: "channel-escalations", label: "Channel-wise Escalations" },
  { key: "resolved", label: "Resolved Escalations" },
  { key: "rejected", label: "Rejected Claims / Escalations" },
  { key: "order-history", label: "Order-wise Escalation History" },
  { key: "date-summary", label: "Date Summary" },
];

// Convert axios error into a readable message without ever faking success
export const errMsg = (err, fallback = "Request failed") =>
  (err && err.response && err.response.data && (err.response.data.message || err.response.data.error)) ||
  (err && err.message) ||
  fallback;

// ── Dashboard ──────────────────────────────────
export const fetchKpis = async (token) => {
  const res = await axios.get(`${api}/api/escalations/kpis`, { headers: getHeaders(token) });
  return res.data;
};

export const fetchOptions = async (token) => {
  const res = await axios.get(`${api}/api/escalations/options`, { headers: getHeaders(token) });
  return res.data;
};

// ── Register list ──────────────────────────────
export const fetchCases = async (token, params = {}) => {
  const res = await axios.get(`${api}/api/escalations`, { params, headers: getHeaders(token) });
  return res.data;
};

export const fetchCase = async (token, id) => {
  const res = await axios.get(`${api}/api/escalations/${id}`, { headers: getHeaders(token) });
  return res.data.record;
};

// ── Validation & warnings ─────────────────────
export const validateOrder = async (token, { orderId = "", awb = "" } = {}) => {
  const res = await axios.get(`${api}/api/escalations/validate-order`, {
    params: { orderId, awb },
    headers: getHeaders(token),
  });
  return res.data;
};

export const checkDuplicates = async (token, { salesOrderId = "", awb = "", carrier = "", issue = "" } = {}) => {
  const res = await axios.get(`${api}/api/escalations/duplicate-check`, {
    params: { salesOrderId, awb, carrier, issue },
    headers: getHeaders(token),
  });
  return res.data;
};

// ── Mutations ─────────────────────────────────
// payload: plain object; files: optional File[] appended as multipart attachments
const buildBody = (payload, files) => {
  if (!files || files.length === 0) return payload;
  const fd = new FormData();
  Object.entries(payload).forEach(([k, v]) => {
    if (v !== undefined && v !== null) fd.append(k, v instanceof Date ? v.toISOString() : v);
  });
  files.forEach((f) => fd.append("attachments", f));
  return fd;
};

export const createCase = async (token, payload, files) => {
  const res = await axios.post(`${api}/api/escalations`, buildBody(payload, files), {
    headers: getHeaders(token),
  });
  return res.data;
};

export const updateCase = async (token, id, payload, files) => {
  const res = await axios.put(`${api}/api/escalations/${id}`, buildBody(payload, files), {
    headers: getHeaders(token),
  });
  return res.data;
};

export const addFollowUp = async (token, id, payload, files) => {
  const res = await axios.post(`${api}/api/escalations/${id}/followups`, buildBody(payload, files), {
    headers: getHeaders(token),
  });
  return res.data;
};

export const resolveCase = async (token, id, payload) => {
  const res = await axios.post(`${api}/api/escalations/${id}/resolve`, payload, { headers: getHeaders(token) });
  return res.data;
};

export const rejectCase = async (token, id, payload) => {
  const res = await axios.post(`${api}/api/escalations/${id}/reject`, payload, { headers: getHeaders(token) });
  return res.data;
};

// ── History & reports ─────────────────────────
export const fetchOrderHistory = async (token, key) => {
  const res = await axios.get(`${api}/api/escalations/order-history/${encodeURIComponent(key)}`, {
    headers: getHeaders(token),
  });
  return res.data;
};

export const fetchReport = async (token, type, params = {}) => {
  const res = await axios.get(`${api}/api/escalations/reports/${type}`, { params, headers: getHeaders(token) });
  return res.data.report;
};

// Downloads an authenticated .xlsx blob and triggers the browser save dialog
export const downloadExport = async (token, urlPath, params = {}, filename = "escalations-export.xlsx") => {
  const res = await axios.get(`${api}${urlPath}`, {
    params,
    headers: getHeaders(token),
    responseType: "blob",
  });
  const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement("a");
  link.href = blobUrl;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
  return true;
};

